import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ShoppingCart, FileText, Package, HelpCircle, Calendar, Bell, Plus, DollarSign, BarChart3, FolderOpen, MessageSquare, Monitor, Globe, Scale, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  getBlogPosts, getAdminProducts, getAllBookings, getCalendarData,
  getEvents, getAdminOrders, getAnalytics, getNotices, getSiteSettings,
  getApplications, getAboutUsContent, getLegalPages, getFAQs
} from '../utils/api';
import { toast } from 'sonner';
import axios from 'axios';

import {
  AdminDashboard, AdminUsers, AdminProducts, AdminBookings, AdminOrders,
  AdminBlog, AdminForum, AdminEvents, AdminNotices, AdminSettings,
  AdminApplications, AdminAboutUs, AdminLegalPages, AdminFAQs,
  AdminRevenue, AdminHowTo
} from '../components/admin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [productsSubTab, setProductsSubTab] = useState('bookings');
  const [resourcesSubTab, setResourcesSubTab] = useState('blog');

  // Data state
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [forumTopics, setForumTopics] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [notices, setNotices] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [calendarData, setCalendarData] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // CMS state
  const [cmsApplications, setCmsApplications] = useState([]);
  const [aboutUsContent, setAboutUsContent] = useState({ who_we_are: '', our_focus: '', our_logo_description: '', our_logo_url: '' });
  const [legalPages, setLegalPages] = useState({ terms_of_use: {}, privacy_policy: {}, copyright_policy: {} });
  const [cmsFaqs, setCmsFaqs] = useState([]);

  // Settings
  const [siteSettings, setSiteSettings] = useState({
    office_address: '', office_hours: '', office_phone: '', office_email: '',
    facebook_url: '', instagram_url: '', tiktok_url: '', twitter_url: '', linkedin_url: '', youtube_url: ''
  });

  // Stats
  const [stats, setStats] = useState({
    totalUsers: 0, totalProducts: 0, totalBlogPosts: 0, totalBookings: 0,
    totalOrders: 0, pendingBookings: 0, pendingOrders: 0
  });

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [
        blogRes, productsRes, bookingsRes, calRes,
        eventsRes, ordersRes, analyticsRes, usersRes,
        noticesRes, settingsRes, announcementsRes, forumRes,
        appsRes, aboutRes, legalRes, faqsRes
      ] = await Promise.allSettled([
        getBlogPosts(),
        getAdminProducts(),
        getAllBookings(),
        getCalendarData(currentMonth, currentYear),
        getEvents(true),
        getAdminOrders(),
        getAnalytics(),
        axios.get(`${API_URL}/api/admin/users`, { headers }),
        getNotices(),
        getSiteSettings(),
        axios.get(`${API_URL}/api/announcements`, { headers }),
        axios.get(`${API_URL}/api/forum/topics`),
        getApplications(true),
        getAboutUsContent(),
        getLegalPages(),
        getFAQs(true),
      ]);

      if (blogRes.status === 'fulfilled') setBlogPosts(blogRes.value.data || []);
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data || []);
      if (bookingsRes.status === 'fulfilled') setBookings(bookingsRes.value.data || []);
      if (calRes.status === 'fulfilled') setCalendarData(calRes.value.data || {});
      if (eventsRes.status === 'fulfilled') setEvents(eventsRes.value.data || []);
      if (ordersRes.status === 'fulfilled') setOrders(ordersRes.value.data || []);
      if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value.data || {});
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data || []);
      if (noticesRes.status === 'fulfilled') setNotices(noticesRes.value.data || []);
      if (settingsRes.status === 'fulfilled') {
        const s = settingsRes.value.data || {};
        setSiteSettings({
          office_address: s.office_address || '', office_hours: s.office_hours || '',
          office_phone: s.office_phone || '', office_email: s.office_email || '',
          facebook_url: s.social_links?.facebook || s.facebook_url || '',
          instagram_url: s.social_links?.instagram || s.instagram_url || '',
          tiktok_url: s.social_links?.tiktok || s.tiktok_url || '',
          twitter_url: s.social_links?.twitter || s.twitter_url || '',
          linkedin_url: s.social_links?.linkedin || s.linkedin_url || '',
          youtube_url: s.social_links?.youtube || s.youtube_url || '',
        });
      }
      if (announcementsRes.status === 'fulfilled') setAnnouncements(announcementsRes.value.data || []);
      if (forumRes.status === 'fulfilled') setForumTopics(forumRes.value.data || []);
      if (appsRes.status === 'fulfilled') setCmsApplications(appsRes.value.data || []);
      if (aboutRes.status === 'fulfilled') {
        const a = aboutRes.value.data;
        if (a) setAboutUsContent({
          who_we_are: a.who_we_are || '', our_focus: a.our_focus || '',
          our_logo_description: a.our_logo_description || '', our_logo_url: a.our_logo_url || ''
        });
      }
      if (legalRes.status === 'fulfilled') {
        const pages = legalRes.value.data || [];
        const lp = { terms_of_use: {}, privacy_policy: {}, copyright_policy: {} };
        pages.forEach(p => { if (lp[p.page_type] !== undefined) lp[p.page_type] = p; });
        setLegalPages(lp);
      }
      if (faqsRes.status === 'fulfilled') setCmsFaqs(faqsRes.value.data || []);

      // Update stats
      const u = usersRes.status === 'fulfilled' ? (usersRes.value.data || []) : users;
      const p = productsRes.status === 'fulfilled' ? (productsRes.value.data || []) : products;
      const b = blogRes.status === 'fulfilled' ? (blogRes.value.data || []) : blogPosts;
      const bk = bookingsRes.status === 'fulfilled' ? (bookingsRes.value.data || []) : bookings;
      const o = ordersRes.status === 'fulfilled' ? (ordersRes.value.data || []) : orders;
      setStats({
        totalUsers: u.length, totalProducts: p.length, totalBlogPosts: b.length,
        totalBookings: bk.length, totalOrders: o.length,
        pendingBookings: bk.filter(x => x.status === 'pending' || x.status === 'payment_uploaded').length,
        pendingOrders: o.filter(x => x.status === 'payment_uploaded').length,
      });
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  const loadCalendarData = async (month, year) => {
    try {
      const res = await getCalendarData(month, year);
      setCalendarData(res.data || {});
    } catch (error) {
      console.error('Error loading calendar data:', error);
    }
  };

  useEffect(() => {
    if (!authLoading && user && user.is_admin) loadData();
    if (!authLoading && (!user || !user.is_admin)) navigate('/');
  }, [authLoading, user]);

  if (!user || !user.is_admin) return null;

  const tabBtn = (id, icon, label, badge) => (
    <button key={id} onClick={() => setActiveTab(id)}
      className={`pb-4 px-4 font-medium transition-colors whitespace-nowrap ${activeTab === id ? 'text-[#8B1818] border-b-2 border-[#8B1818]' : 'text-slate-600 hover:text-slate-900'}`}>
      {icon}{label}{badge > 0 && <span className="ml-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs">{badge}</span>}
    </button>
  );

  const subTabBtn = (id, icon, label, active, onClick, badge) => (
    <button key={id} onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${active ? 'bg-[#8B1818] text-white' : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'}`}>
      {icon}{label}{badge > 0 && <span className="ml-1 px-2 py-0.5 bg-yellow-400 text-yellow-900 rounded-full text-xs">{badge}</span>}
    </button>
  );

  return (
    <div className="min-h-screen py-12 bg-slate-50" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Playfair Display, serif'}} data-testid="admin-heading">Admin Dashboard</h1>
          <p className="text-slate-600">Welcome back, {user.name}</p>
        </div>

        {/* Main Tabs */}
        <div className="flex space-x-2 mb-6 border-b border-slate-200 overflow-x-auto">
          {tabBtn('dashboard', <BarChart3 className="inline h-4 w-4 mr-2" />, 'Analytics')}
          {tabBtn('users', <Users className="inline h-4 w-4 mr-2" />, `Users (${stats.totalUsers})`)}
          {tabBtn('products-group', <Package className="inline h-4 w-4 mr-2" />, 'Products', stats.pendingBookings + stats.pendingOrders)}
          {tabBtn('resources-group', <FolderOpen className="inline h-4 w-4 mr-2" />, 'Resources')}
          {tabBtn('revenue', <DollarSign className="inline h-4 w-4 mr-2" />, 'Revenue')}
          {tabBtn('howto', <HelpCircle className="inline h-4 w-4 mr-2" />, 'How To')}
        </div>

        {/* Products Sub-tabs */}
        {activeTab === 'products-group' && (
          <div className="flex gap-2 mb-6 flex-wrap">
            {subTabBtn('bookings', <Calendar className="h-4 w-4" />, 'Bookings', productsSubTab === 'bookings', () => setProductsSubTab('bookings'), stats.pendingBookings)}
            {subTabBtn('add-products', <Plus className="h-4 w-4" />, `Add Products (${stats.totalProducts})`, productsSubTab === 'add-products', () => setProductsSubTab('add-products'))}
            {subTabBtn('orders', <ShoppingCart className="h-4 w-4" />, 'Orders', productsSubTab === 'orders', () => setProductsSubTab('orders'), stats.pendingOrders)}
          </div>
        )}

        {/* Resources Sub-tabs */}
        {activeTab === 'resources-group' && (
          <div className="flex gap-2 mb-6 flex-wrap">
            {subTabBtn('blog', <FileText className="h-4 w-4" />, `Blog Posts (${stats.totalBlogPosts})`, resourcesSubTab === 'blog', () => setResourcesSubTab('blog'))}
            {subTabBtn('forum', <MessageSquare className="h-4 w-4" />, `Forum Topics (${forumTopics.length})`, resourcesSubTab === 'forum', () => setResourcesSubTab('forum'))}
            {subTabBtn('events', <Calendar className="h-4 w-4" />, 'Events', resourcesSubTab === 'events', () => setResourcesSubTab('events'))}
            {subTabBtn('notices', <Bell className="h-4 w-4" />, `Notices (${notices.length})`, resourcesSubTab === 'notices', () => setResourcesSubTab('notices'))}
            {subTabBtn('applications', <Monitor className="h-4 w-4" />, `Applications (${cmsApplications.length})`, resourcesSubTab === 'applications', () => setResourcesSubTab('applications'))}
            {subTabBtn('about-us', <Globe className="h-4 w-4" />, 'About Us', resourcesSubTab === 'about-us', () => setResourcesSubTab('about-us'))}
            {subTabBtn('legal-pages', <Scale className="h-4 w-4" />, 'Legal Pages', resourcesSubTab === 'legal-pages', () => setResourcesSubTab('legal-pages'))}
            {subTabBtn('faqs', <HelpCircle className="h-4 w-4" />, `FAQs (${cmsFaqs.length})`, resourcesSubTab === 'faqs', () => setResourcesSubTab('faqs'))}
            {subTabBtn('settings', <Info className="h-4 w-4" />, 'Contact & Social', resourcesSubTab === 'settings', () => setResourcesSubTab('settings'))}
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <AdminDashboard analytics={analytics} stats={stats} setActiveTab={setActiveTab} setResourcesSubTab={setResourcesSubTab} setProductsSubTab={setProductsSubTab} />
        )}

        {activeTab === 'users' && (
          <AdminUsers users={users} announcements={announcements} loadData={loadData} />
        )}

        {activeTab === 'products-group' && productsSubTab === 'bookings' && (
          <AdminBookings bookings={bookings} calendarData={calendarData} stats={stats}
            currentMonth={currentMonth} currentYear={currentYear} loadData={loadData}
            onCalendarChange={(m, y) => { setCurrentMonth(m); setCurrentYear(y); loadCalendarData(m, y); }} />
        )}

        {activeTab === 'products-group' && productsSubTab === 'add-products' && (
          <AdminProducts products={products} loadData={loadData} />
        )}

        {activeTab === 'products-group' && productsSubTab === 'orders' && (
          <AdminOrders orders={orders} loadData={loadData} />
        )}

        {activeTab === 'resources-group' && resourcesSubTab === 'blog' && (
          <AdminBlog blogPosts={blogPosts} userName={user?.name} loadData={loadData} />
        )}

        {activeTab === 'resources-group' && resourcesSubTab === 'forum' && (
          <AdminForum forumTopics={forumTopics} loadData={loadData} />
        )}

        {activeTab === 'resources-group' && resourcesSubTab === 'events' && (
          <AdminEvents events={events} loadData={loadData} />
        )}

        {activeTab === 'resources-group' && resourcesSubTab === 'notices' && (
          <AdminNotices notices={notices} loadData={loadData} />
        )}

        {activeTab === 'resources-group' && resourcesSubTab === 'settings' && (
          <AdminSettings siteSettings={siteSettings} loadData={loadData} />
        )}

        {activeTab === 'resources-group' && resourcesSubTab === 'applications' && (
          <AdminApplications cmsApplications={cmsApplications} loadData={loadData} />
        )}

        {activeTab === 'resources-group' && resourcesSubTab === 'about-us' && (
          <AdminAboutUs aboutUsContent={aboutUsContent} loadData={loadData} />
        )}

        {activeTab === 'resources-group' && resourcesSubTab === 'legal-pages' && (
          <AdminLegalPages legalPages={legalPages} loadData={loadData} />
        )}

        {activeTab === 'resources-group' && resourcesSubTab === 'faqs' && (
          <AdminFAQs cmsFaqs={cmsFaqs} loadData={loadData} />
        )}

        {activeTab === 'revenue' && (
          <AdminRevenue bookings={bookings} orders={orders} />
        )}

        {activeTab === 'howto' && (
          <AdminHowTo />
        )}
      </div>
    </div>
  );
};

export default Admin;
